import React from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../store/authStore";

interface GoogleButtonProps {
  /** The translation key for the button text (e.g., 'googleButton.signIn' or 'googleButton.signUp') */
  textKey?: string;
  className?: string;
  /** Optional callback function to handle errors during sign-in */
  onError?: (error: Error) => void;
  /** Size variant - default is 'large' for main CTA */
  size?: "large" | "medium" | "small";
}

const GoogleButton: React.FC<GoogleButtonProps> = ({
  textKey = "googleButton.signUp",
  className = "",
  onError,
  size = "large",
}) => {
  const { t } = useTranslation();
  const { signInWithGoogle, isLoading } = useAuthStore();

  const handleGoogleAction = async () => {
    console.log("GoogleButton: Calling signInWithGoogle...");
    try {
      await signInWithGoogle();
      console.log(
        "GoogleButton: signInWithGoogle initiated successfully (or redirected)."
      );
    } catch (err) {
      console.error("Google Sign-In error:", err);
      if (onError && err instanceof Error) {
        onError(err);
      } else if (err instanceof Error) {
        console.error("Unhandled Google sign-in error:", err.message);
      } else {
        console.error("Unhandled non-Error during Google sign-in:", err);
        if (onError) {
          onError(new Error(t("loginPage.googleSignInError")));
        }
      }
    }
  };

  // Size-based classes
  const sizeClasses = {
    large: "py-4 px-8 text-xl",
    medium: "py-3 px-6 text-lg",
    small: "py-2 px-4 text-base",
  };

  // Enhanced default styling with stronger CTA appearance
  const defaultClasses = `
    w-full
    flex items-center justify-center
    bg-blue-600 hover:bg-blue-700 active:bg-blue-800
    text-white font-bold
    rounded-lg
    shadow-lg hover:shadow-xl
    border-2 border-blue-400
    focus:outline-none focus:ring-4 focus:ring-blue-300
    disabled:opacity-70 disabled:cursor-not-allowed
    transform hover:scale-105 active:scale-95
    transition-all duration-200
    ${sizeClasses[size]}
  `;

  const loadingSpinner = (
    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const googleIcon = (
    <div className="bg-white p-1 rounded-md mr-3">
      <svg
        className={`${size === "large" ? "h-6 w-6" : "h-5 w-5"}`}
        viewBox="0 0 24 24"
      >
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
        <path d="M1 1h22v22H1z" fill="none" />
      </svg>
    </div>
  );

  return (
    <button
      onClick={handleGoogleAction}
      disabled={isLoading}
      className={className || defaultClasses}
      aria-label={`${t(textKey)} with Google`}
      style={{
        backgroundColor: "#4285F4",
        color: "white",
        border: "none",
        borderRadius: "10px",
        padding: "10px 20px",
        fontSize: "16px",
        fontWeight: "bold",
        cursor: isLoading ? "not-allowed" : "pointer",
        opacity: isLoading ? 0.7 : 1,
        transition: "all 0.3s ease",
      }}
    >
      {isLoading ? loadingSpinner : googleIcon}
      <span className="">
        {isLoading ? t("googleButton.signingIn", "Signing in...") : t(textKey)}
      </span>
    </button>
  );
};

export default GoogleButton;
